import React, { useEffect, useRef, useState } from 'react';
import { Errors } from 'components/ui/errors';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import {
  faArrowLeft,
  faArrowRight,
  faBuilding,
  faCheck,
  faCircleUser,
  faHandshake,
  faTrash,
  faUserCircle
} from '@fortawesome/pro-regular-svg-icons';
import { Placeholder } from '@buildoutinc/blueprint-react/ui/Placeholder';

import AttachmentsSection from './components/attachments';
import { CalendarPortal } from 'components/form/managed_input/date_input';
import { Button } from 'components/ui/button';
import Contacts from './components/contacts';
import DateInput from 'bundles/apex/dates/input';
import DeleteModal from 'bundles/apex/tasks/delete_modal';
import ImmediateRemindersModal from 'bundles/apex/deals/show/critical_dates/modal/immediate_reminders';
import ManagedInput from 'components/form/managed_input';
import ManagedFormGroup from 'components/form/managed_input/form_group';
import Modal from 'components/modal';
import confirm from 'components/confirm';
import ParentSearch from './components/parent_search';
import TaskRemindersFields from './components/reminders_fields';
import Section from './components/section';
import Tooltip from 'components/tooltip';
import { useTaskModalState } from './helpers';
import type {
  Attachment, ForUser, ModalOptions, SelectableParentType, Task, TaskAttributes, TaskFormState
} from '../types';
import type { ParentOption } from 'bundles/crm/types';
import type { Reminder } from 'bundles/apex/reminders/types';

import 'bundles/apex/tasks/styles.scss';
import { showSnackbar } from 'components/ui/snackbar/helper';


const SOURCE_TYPE_OPTIONS: { label: string, value: SelectableParentType }[] = [
  { label: 'Deal', value: 'Apex::Deal' },
  { label: 'Contact', value: 'Backoffice::Contact' },
];

interface TaskFormModalProps {
  allowParentSelection?: boolean;
  attachmentsPath?: string;
  canDeleteAttachments?: boolean;
  checkImmediateRemindersPath?: string;
  createTask?: (task: TaskAttributes) => Promise<any>;
  currentTask?: Task;
  disableAssignee?: boolean;
  formDependenciesPath?: string;
  forUser?: ForUser;
  hasNext?: boolean;
  hasPrevious?: boolean;
  hideStatusButton?: boolean;
  loading?: boolean;
  options?: ModalOptions;
  reminders?: Reminder[];
  show: boolean;
  showAdditionalOptions?: boolean;
  showType?: boolean;
  task?: Task | null,
  updateTask?: (task: TaskAttributes) => Promise<any>;
  onAddAttachment?: (task: Task, attachment: Attachment) => void;
  onDelete?: (task: Task) => void | Promise<void>;
  onDeleteAttachment?: (task: Task, attachment: Attachment) => void;
  onHide: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onSave?: () => void;
  reloadTasks?: () => void;
}

export default function TaskFormModal({
  allowParentSelection,
  attachmentsPath,
  canDeleteAttachments,
  checkImmediateRemindersPath,
  createTask,
  currentTask,
  disableAssignee,
  formDependenciesPath,
  forUser,
  hasNext,
  hasPrevious,
  hideStatusButton,
  options,
  reminders,
  show,
  showAdditionalOptions,
  showType = true,
  task,
  updateTask,
  onAddAttachment,
  onDelete,
  onDeleteAttachment,
  onHide,
  onNext,
  onPrevious,
  onSave,
  reloadTasks
}: TaskFormModalProps) {
  const {
    contactsTasks,
    dependencies,
    errors,
    formTask,
    handleSave,
    handleChange,
    handleRemindersChange,
    hasChanges,
    hide,
    immediateReminders,
    loading,
    reset,
    requestReminders,
    saving,
    setContactsTasks,
    setImmediateReminders,
    typeOptions,
    userNames,
  } = useTaskModalState({
    allowParentSelection,
    checkImmediateRemindersPath,
    createTask,
    formDependenciesPath,
    forUser,
    options,
    reminders,
    show,
    updateTask,
    onHide,
    onSave
  });
  const [disableRequiresAttachment, setDisableRequiresAttachment] = useState(false);
  const [selectedParent, setSelectedParent] = useState<ParentOption | null>(null);
  const [followUpChecked, setFollowUpChecked] = useState(Boolean(formTask?.followUpDate));
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const showParentPicker = Boolean(allowParentSelection && !task);
  const missingAttachment = formTask?.requiresAttachment && !formTask.attachments?.length;
  const showAttachments = attachmentsPath && forUser && currentTask && onAddAttachment && onDeleteAttachment;
  const showNav = Boolean(task && (onNext || onPrevious));
  const showStatusButton = Boolean(task?.id) && !hideStatusButton;
  const { assignableUsers, contacts, deal, fields } = (dependencies || { assignableUsers: [], contacts: [] });
  const errorsRef = useRef<HTMLDivElement | null>(null);

  const handleStatusChange = async (newStatus: boolean) => {
    const changes = { completed: newStatus };
    handleChange(changes);
    await handleSave(changes, null);
    showSnackbar({ text: 'Task Updated', variant: 'success' });
    if (newStatus && reloadTasks && formTask.followUpDate) {
      setTimeout(() => {
        reloadTasks();
        setFollowUpChecked(false);
        handleChange({ followUpDate: '' });
      }, 500);
    }
  };

  const handleDateChange = (date) => {
    const newDeadline = typeof date === 'string' ? { ...formTask.deadline, date, static: true } : date;
    handleChange({ deadline: newDeadline });
  };

  const handleContactsChange = (contactsTasks) => {
    setContactsTasks(contactsTasks);
    handleChange({ contactsTasks });
  };

  const handleFollowUpCheck = () => {
    setFollowUpChecked(!followUpChecked);
    handleChange({ followUpDate: '' });
  };

  const handleRequiresAttachmentChange = () => {
    handleChange({ requiresAttachment: !formTask.requiresAttachment });
  };

  const handleAttachmentAdded = (task: Task, attachment: Attachment) => {
    if (task) {
      onAddAttachment?.(task, attachment);
      showSnackbar({ text: 'Attachment Added', variant: 'success' });
    }
  };

  const confirmDiscard = async () => {
    if (!hasChanges) return true;
    try {
      await confirm('You have unsaved changes. Are you sure you want to discard them?', {
        buttonLabel: 'Discard Changes',
        buttonStyle: 'danger',
        title: 'Unsaved Changes',
      });
      return true;
    } catch (e) {
      if (e) throw e;
      return false;
    }
  };

  const attemptHide = async () => {
    if (await confirmDiscard()) hide();
  };

  const handleDelete = async () => {
    if (task) await onDelete?.(task);
    hide();
  };

  const attemptPrevious = async () => {
    if (onPrevious && await confirmDiscard()) onPrevious();
  };

  const attemptNext = async () => {
    if (onNext && await confirmDiscard()) onNext();
  };

  const saveTask = async (
    changes?: Partial<TaskFormState>,
    keepReminders?: boolean
  ) => {
    const isCreate = !formTask.id;
    const res = await handleSave({ ...formTask, ...changes }, keepReminders);
    if (!res) return;
    if (res.errors) {
      showSnackbar({ text: 'Something went wrong', variant: 'danger' });
    } else {
      showSnackbar({ text: 'Task Saved', variant: 'success' });
      if (isCreate) hide();
    }
  };

  const renderPlaceholder = (label?: string, placeholderStyle?: React.CSSProperties) => {
    return (
      <div className="d-flex flex-column" style={{ gap: '0.25rem' }}>
        {label && <Label className="mb-1">{label}</Label>}
        {/* Match the height of a Bootstrap .form-control input (line-height + padding + borders) */}
        <Placeholder
          className="w-100"
          style={{ height: 'calc(1.5em + 0.75rem + 2px)', borderRadius: '0.25rem', ...placeholderStyle }}
        />
      </div>
    );
  };

  let statusButton = (
    <Button
      disabled={loading}
      icon={faCheck}
      label={task?.completed ? 'Mark as Incomplete' : 'Mark as Complete'}
      variant="secondary"
      onClick={() => handleStatusChange(!task?.completed)}
    />
  );
  if (!task?.completed && missingAttachment) {
    statusButton = (
      <Tooltip content="Upload an attachment to complete task" dark placement="top">
        {statusButton}
      </Tooltip>
    );
  }

  let parentLabel, parentIcon;
  if (dependencies?.parent?.className === 'Apex::Deal') {
    parentLabel = 'Deal';
    parentIcon = faHandshake;
  } else if (dependencies?.parent?.className === 'Backoffice::Contact') {
    parentLabel = 'Contact';
    parentIcon = faCircleUser;
  } else if (dependencies?.parent?.className === 'Project') {
    parentLabel = 'Listing';
    parentIcon = faBuilding;
  }

  const parentDisplayName = task?.parent?.label
    || (dependencies?.parent?.className === 'Backoffice::Contact'
      ? dependencies.parent.name
      : dependencies?.parent?.displayName);

  const assigneeOptions: { label: string, value: '' | number }[] = [
    { label: 'None', value: '' },
  ];

  if (currentTask?.assignee?.id && !assignableUsers.find(user => user.id === currentTask.assignee?.id)) {
    assigneeOptions.push({ label: currentTask.assignee.name, value: currentTask.assignee.id });
  }

  assigneeOptions.push(...assignableUsers.map(user => ({ label: user.name, value: user.id })));

  useEffect(() => {
    // The modal can open over any page (e.g. from the header's New menu), so it defers to the page's own
    // unsaved-changes guard rather than replacing it, and hands it back when it closes.
    const pageHandler = window.onbeforeunload;

    window.onbeforeunload = function onBeforeUnload(event) {
      // TODO: This won't work when using the back button to navigate to a different tab on the same page
      // Add deal page solution for this.
      if (show && task && hasChanges) {
        return 'There are unsaved changes. Are you sure you want to leave this page?';
      }

      return pageHandler?.call(window, event);
    };

    return () => {
      window.onbeforeunload = pageHandler;
    };
  }, [show && task && hasChanges]);

  useEffect(() => {
    reset(task);
    setContactsTasks(task?.contactsTasks || []);
    setSelectedParent(null);
    setFollowUpChecked(Boolean(task?.followUpDate));
  }, [task?.id]);

  useEffect(() => {
    if (!loading) {
      setDisableRequiresAttachment(!!(task?.id));
    }
  }, [loading]);

  useEffect(() => {
    if (errors?.length) {
      errorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errors]);

  return <>
    <Modal
      enforceFocus={false}
      scrollable
      show={show}
      onHide={attemptHide}
      onShow={() => reset(task)}
    >
      <Modal.Header closeButton>
        {formTask.id ? 'Edit Task' : 'Add Task'}
      </Modal.Header>
      <Modal.Body className="d-flex flex-column gap-3">
        <Errors errors={errors} ref={errorsRef} />

        {loading ? renderPlaceholder('Task Name') : (
          <ManagedFormGroup
            label="Task Name"
            name="name"
            required
            value={formTask.name}
            onChange={handleChange}
          />
        )}

        {loading ? renderPlaceholder('Assigned To') : (
          <ManagedFormGroup
            className="mt-0"
            icon={faUserCircle}
            isDisabled={disableAssignee}
            label="Assigned To"
            name="assigneeId"
            options={assigneeOptions}
            type="combobox"
            value={assigneeOptions.find(option => option.value === formTask.assigneeId) ?? null}
            onValueChange={option => handleChange({ assigneeId: option.value })}
          />
        )}
        <div>
          <Label className="mb-1">Due Date</Label>
          <div>
            {loading ? renderPlaceholder() : deal
              ? <>
                <DateInput
                  deal={deal}
                  fields={fields}
                  name="deadline"
                  popperContainer={CalendarPortal}
                  popperPlacement="bottom-end"
                  value={formTask.deadline}
                  wrapperClassName="w-100"
                  onValueChange={handleDateChange}
                />
              </> : (
                <ManagedInput
                  className="form-control text-end"
                  name="deadline"
                  popperContainer={CalendarPortal}
                  popperPlacement="bottom-end"
                  required
                  type="date"
                  value={formTask.deadline?.date}
                  wrapperClassName="w-100"
                  onValueChange={handleDateChange}
                />
              )
            }
          </div>
        </div>

        {(showType || showParentPicker || parentLabel) && (
          <div className="d-flex gap-2">
            {showType && (
              <div className="flex-grow-1">
                {loading ? renderPlaceholder('Type') : (
                  <ManagedFormGroup
                    label="Type"
                    name="taskType"
                    options={typeOptions || []}
                    type="combobox"
                    value={(typeOptions || []).find(option => option.value === formTask.taskType) ?? null}
                    onValueChange={option => handleChange({ taskType: option.value })}
                  />
                )}
              </div>
            )}
            {(showParentPicker || parentLabel) && (
              <div className="flex-grow-1">
                {loading ? renderPlaceholder('Source') : (
                  <ManagedFormGroup
                    isDisabled={!showParentPicker}
                    label="Source"
                    name="selectedParentType"
                    options={showParentPicker
                      ? SOURCE_TYPE_OPTIONS
                      : [{ label: parentLabel, value: parentLabel }]}
                    type="combobox"
                    value={showParentPicker
                      ? SOURCE_TYPE_OPTIONS.find(option => option.value === formTask.selectedParentType) ?? null
                      : { label: parentLabel, value: parentLabel }}
                    onValueChange={(option) => {
                      handleChange({ selectedParentType: option.value });
                      setSelectedParent(null);
                    }}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {showParentPicker && formTask.selectedParentType && (
          <div>
            {loading ? renderPlaceholder('Select Source') : (
              <>
                <Label className="mb-1">
                  {SOURCE_TYPE_OPTIONS.find(o => o.value === formTask.selectedParentType)?.label}
                </Label>
                <ParentSearch
                  key={formTask.selectedParentType}
                  parentType={formTask.selectedParentType}
                  value={selectedParent}
                  onChange={(option) => {
                    setSelectedParent(option);
                    handleChange({ parentOptionValue: option?.value });
                  }}
                />
              </>
            )}
          </div>
        )}

        {(!showParentPicker && !options?.parents && dependencies?.parent && parentLabel && parentIcon) && (
          loading ? renderPlaceholder(parentLabel) : (
            <ManagedFormGroup
              buttonIcon={parentIcon}
              disabled
              label={parentLabel}
              options={[{ label: parentDisplayName, value: parentLabel }]}
              type="select"
              value={parentLabel}
            >
              <option label={parentDisplayName} value={parentLabel} />
            </ManagedFormGroup>
          )
        )}

        {options?.parents && options.parents.length > 1 &&
          <ManagedFormGroup
            label="Listing"
            name="parentOptionValue"
            options={options.parents}
            required
            type="combobox"
            value={options.parents.find(option => option.value === formTask.parentOptionValue) ?? null}
            onValueChange={option => handleChange({ parentOptionValue: option.value })}
          />
        }

        {contacts.length > 0 && (
          <Contacts
            contacts={contacts}
            contactsTasks={contactsTasks}
            onChange={handleContactsChange}
          />
        )}

        {loading ? renderPlaceholder('Notes', { height: 100 }) : (
          <ManagedFormGroup
            autoHeight
            className="mt-0"
            inputClassName="task-modal-notes"
            label="Notes"
            rows={5}
            type="textarea"
            value={formTask.notes || ''}
            onValueChange={value => handleChange({ notes: value })}
          />
        )}

        <TaskRemindersFields
          deal={deal}
          fields={fields}
          reminders={requestReminders}
          task={formTask}
          onChange={reminders => handleRemindersChange(reminders as Reminder[])}
        />
        <ImmediateRemindersModal
          handleSelection={keepReminders => saveTask(
            { deadline: formTask.deadline, reminders: requestReminders }, keepReminders
          )}
          show={show && immediateReminders.length > 0}
          userNames={userNames}
          onHide={() => setImmediateReminders([])}
        />


        {showAdditionalOptions && (
          <>
            <h6 className="fs-large fw-semibold">Additional Options</h6>
            {!task?.completed && (
              <>
                <ManagedInput
                  checked={followUpChecked}
                  id="follow-up-checkbox"
                  label="Create a Follow-Up"
                  type="checkbox"
                  onChange={handleFollowUpCheck}
                />
                {followUpChecked && (
                  <ManagedFormGroup
                    label="Follow Up Date"
                    name="follow-up"
                    popperContainer={CalendarPortal}
                    popperPlacement="top-end"
                    type="date"
                    value={formTask.followUpDate || ''}
                    onValueChange={date => handleChange({ followUpDate: date })}
                  />
                )}
              </>
            )}
          </>
        )}
        <ManagedInput
          checked={formTask.requiresAttachment}
          className="mt-1"
          disabled={disableRequiresAttachment}
          label="Require Attachments?"
          labelClassName="d-flex justify-content-start gap-2"
          name="requiresAttachment"
          type="checkbox"
          onChange={handleRequiresAttachmentChange}
        >
          <div className="d-flex flex-column align-items-start">
            Require Attachments?
            <small className="text-muted">Does this task need an attachment in order to be completed?</small>
          </div>
        </ManagedInput>

        {showAttachments &&
          <>
            <Section className="m-0" label="Attachments" />
            <AttachmentsSection
              attachmentsPath={attachmentsPath}
              canDeleteAttachments={canDeleteAttachments}
              forUserId={forUser.id}
              task={currentTask}
              onAddAttachment={handleAttachmentAdded}
              onDeleteAttachment={onDeleteAttachment}
            />
          </>
        }

        {task && onDelete && (
          <div className="d-flex justify-content-center">
            <Button
              icon={faTrash}
              label="Delete Task"
              variant="ghost"
              onClick={() => setShowDeleteModal(true)}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex align-items-center">
        {(showNav || showStatusButton) && (
          <div className="d-flex align-items-center gap-2">
            {showNav && (
              <Button
                disabled={!hasPrevious || !onPrevious || loading}
                disabledTooltip={(!hasPrevious || !onPrevious) ? 'No Previous Item' : undefined}
                icon={faArrowLeft}
                variant="ghost"
                onClick={attemptPrevious}
              />
            )}
            {showStatusButton && statusButton}
            {showNav && (
              <Button
                disabled={!hasNext || !onNext || loading}
                disabledTooltip={(!hasNext || !onNext) ? 'No Next Item' : undefined}
                icon={faArrowRight}
                variant="ghost"
                onClick={attemptNext}
              />
            )}
          </div>
        )}
        <div className="d-flex gap-2 ms-auto">
          <Button name="cancel" variant="ghost" onClick={attemptHide}>
            Cancel
          </Button>
          <Button disabled={!hasChanges || saving} label="Save" onClick={() => saveTask({ ...formTask })} />
        </div>
      </Modal.Footer>
    </Modal>

    <DeleteModal
      show={showDeleteModal}
      onDelete={handleDelete}
      onHide={() => setShowDeleteModal(false)}
    />
  </>;
}
